/**
 * PREVIEW: rail panels for the CCC header menu preview
 * (settings.ccc_preview_menu_tree). Adapted from Phaser's
 * assets/custom-phaser-system-picker.js (the left-rail picker).
 *
 * Desktop: a menu item with [data-ccc-rail] has rail items [data-ccc-rail-item]
 * (plain links; entries with a pane also carry data-ccc-tab) and panes
 * [data-ccc-pane]. Pointer: an entry switches the pane when the pointer rests
 * on it; while the pointer is heading for the pane (aim triangle) or has come
 * back into the rail from the pane, entries it crosses do not switch.
 * Keyboard: the rail is one tab stop; ArrowDown or Space on the top link opens
 * the panel; arrows move along the rail; ArrowRight / Tab go into the pane,
 * ArrowLeft returns; Escape closes and returns focus to the top link.
 * Drawer: plain links only (snippets/ccc-menu-preview-drawer.liquid); it fetches
 * nothing, so this file has no drawer code.
 *
 * Rows: any [data-ccc-slot="<handle>"] inside a shown pane
 * is filled once from /collections/<handle>?section_id=<data-ccc-section or
 * ccc-menu-panel> (Section Rendering API, cached per collection + section).
 * A slot with data-ccc-sort (card panes: Blends, Decaf) adds &sort_by=<value>,
 * so the section receives the collection in that order (best-selling).
 */

const DEFAULT_SECTION = 'ccc-menu-panel';
const STORE_PREFIX = 'cccMenu:v11:';
const STORE_TTL = 10 * 60 * 1000; // a stored pane older than 10 minutes is not used
const STORE_FRESH = 2 * 60 * 1000; // younger than 2 minutes: used as is, no background refetch
/** @type {Map<string, Promise<string | null>>} */
const cache = new Map();

/* ---------- Lazy rows ---------- */

/** @param {string} key */
function readStore(key) {
  try {
    const raw = sessionStorage.getItem(STORE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry.html !== 'string') return null;
    // Too old: fetch again (stock and roast dates move).
    if (!(Date.now() - Number(entry.t) < STORE_TTL)) return null;
    return entry;
  } catch {
    return null;
  }
}

/** @param {string} key @param {string} html */
function writeStore(key, html) {
  try {
    sessionStorage.setItem(STORE_PREFIX + key, JSON.stringify({ t: Date.now(), html }));
  } catch {
    // Storage full or blocked: the in-memory cache still works.
  }
}

/**
 * Fetch a pane and return the inner HTML of its [data-ccc-list] (parsed once,
 * inert, in a template).
 * @param {string} handle
 * @param {string} section
 * @param {string} [sort] - collection sort_by value (e.g. best-selling)
 * @param {boolean} [low] - background prefetch: low fetch priority
 * @returns {Promise<string | null>}
 */
function request(handle, section, sort, low) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let url = `${root}collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(section)}`;
  if (sort) url += `&sort_by=${encodeURIComponent(sort)}`;
  /** @type {RequestInit & { priority?: string }} */
  const init = { credentials: 'same-origin' };
  if (low) init.priority = 'low';
  return fetch(url, init)
    .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
    .then((text) => {
      const tpl = document.createElement('template');
      tpl.innerHTML = text;
      const list = tpl.content.querySelector('[data-ccc-list]');
      return list ? list.innerHTML : null;
    });
}

/**
 * Pane HTML: memory, then sessionStorage (up to 10 min old), then the network.
 * A stored copy is shown at once; when it is over 2 minutes old it is also
 * revalidated in the background, and if it changed, the filled slots for that
 * pane are swapped.
 * @param {string} handle
 * @param {string} section
 * @param {string} [sort]
 * @param {boolean} [low] - background prefetch: low fetch priority
 * @returns {Promise<string | null>}
 */
function fetchList(handle, section, sort, low) {
  const key = listKey(handle, section, sort);
  let pending = cache.get(key);
  if (pending) return pending;
  const stored = readStore(key);
  if (stored) {
    pending = Promise.resolve(stored.html);
    cache.set(key, pending);
    if (Date.now() - Number(stored.t) < STORE_FRESH) return pending;
    request(handle, section, sort, true)
      .then((html) => {
        if (html === null) return;
        writeStore(key, html);
        if (html === stored.html) return;
        cache.set(key, Promise.resolve(html));
        document.querySelectorAll('[data-ccc-filled][data-ccc-key]').forEach((slot) => {
          if (slot instanceof HTMLElement && slot.dataset.cccKey === key) slot.innerHTML = html;
        });
      })
      .catch(() => {});
    return pending;
  }
  pending = request(handle, section, sort, low)
    .then((html) => {
      if (html !== null) writeStore(key, html);
      else cache.delete(key); // Missing section markup must be retryable, like a failed request.
      return html;
    })
    .catch(() => {
      cache.delete(key);
      return null;
    });
  cache.set(key, pending);
  return pending;
}

/** @param {string} handle @param {string} section @param {string} [sort] */
function listKey(handle, section, sort) {
  return sort ? `${section}:${handle}:${sort}` : `${section}:${handle}`;
}

/** @param {ParentNode} scope */
async function fillSlots(scope) {
  const slots = scope.querySelectorAll('[data-ccc-slot]:not([data-ccc-filled]):not([data-ccc-loading])');
  // Nothing to load (the common case on every pointer move): touch nothing,
  // so hovering inside an open panel causes no style or layout work.
  if (!slots.length) return;
  for (const slot of slots) {
    if (!(slot instanceof HTMLElement) || slot.dataset.cccLoading) continue;
    slot.dataset.cccLoading = 'true';
    const section = slot.dataset.cccSection || DEFAULT_SECTION;
    const handle = slot.dataset.cccSlot || '';
    const sort = slot.dataset.cccSort || '';
    const html = await fetchList(handle, section, sort);
    delete slot.dataset.cccLoading;
    // On failure the "View all" link still works; the next hover retries.
    if (html === null) continue;
    // One write: the browser parses straight into the slot (no clone, no reads).
    slot.innerHTML = html;
    slot.dataset.cccKey = listKey(handle, section, sort);
    slot.dataset.cccFilled = 'true';
    slot.removeAttribute('aria-busy');
  }
  // Hidden panes are measured when first shown (activate), not here.
  if (!(scope instanceof Element && scope.closest('[data-ccc-pane][hidden]'))) markContinued(scope);
}

/**
 * Load every pane of a rail (or every accordion of the drawer Coffee item) in
 * parallel, at most 4 requests at a time, the shown pane and the first two
 * entries first. Each pane is injected into its hidden slot as it arrives, so
 * moving along the rail never waits. Runs once per scope.
 * @param {Element} scope
 */
function loadAll(scope) {
  if (!scope || scope.hasAttribute('data-ccc-all')) return;
  scope.setAttribute('data-ccc-all', '');
  const slots = Array.from(scope.querySelectorAll('[data-ccc-slot]'));
  const shown = slots.filter((slot) => slot.closest('[data-ccc-pane]:not([hidden])'));
  const queue = [...shown, ...slots.filter((slot) => !shown.includes(slot))];
  let active = 0;
  const next = () => {
    while (active < 4 && queue.length) {
      const slot = queue.shift();
      const host = slot.closest('[data-ccc-pane], details') || slot.parentElement;
      active += 1;
      fillSlots(host || scope).finally(() => {
        active -= 1;
        next();
      });
    }
  };
  next();
}

/** @type {WeakSet<Element>} rails with a loadAll already scheduled */
const loadAllQueued = new WeakSet();

/**
 * Pointer path: loadAll when the browser is idle (at most 300 ms later), not
 * in the same task as the first open. The hidden panes are ~2,000 nodes; the
 * pane under the pointer still fills at once (activate -> fillSlots).
 * @param {Element} scope
 */
function loadAllSoon(scope) {
  if (!scope || loadAllQueued.has(scope) || scope.hasAttribute('data-ccc-all')) return;
  loadAllQueued.add(scope);
  const run = () => loadAll(scope);
  if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 300 });
  else window.setTimeout(run, 50);
}

/** Keyboard: focus reaching a rail fills all of its panes. @param {Event} event */
function onWarm(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const rail = target.closest('.ccc-nav [data-ccc-rail]');
  if (rail) loadAll(rail);
}

/**
 * A big group that flows into the next column gets its heading repeated there
 * ("Colombia (continued)"). Measured after layout, so only visible panes.
 * @param {ParentNode} scope
 */
function markContinued(scope) {
  requestAnimationFrame(() => {
    scope.querySelectorAll('.ccc-grp__cont').forEach((el) => el.remove());
    scope.querySelectorAll('.ccc-grp--big').forEach((group) => {
      // A pane with a single group shows no headings, so no "(continued)" either.
      if (group.closest('.ccc-grp-set--single')) return;
      const title = group.querySelector('.ccc-grp__title');
      if (!(title instanceof HTMLElement) || title.offsetParent === null) return;
      const left = title.getBoundingClientRect().left;
      const rows = Array.from(group.querySelectorAll('.ccc-grp__list > li')).filter(
        (li) => li instanceof HTMLElement && li.offsetParent !== null
      );
      const first = rows.find((li) => li.getBoundingClientRect().left > left + 40);
      if (!first) return;
      const label = group.querySelector('.ccc-grp__label')?.textContent?.trim() || '';
      const cont = document.createElement('li');
      cont.className = 'ccc-grp__cont';
      cont.setAttribute('aria-hidden', 'true');
      cont.textContent = `${label} (continued)`;
      first.before(cont);
    });
  });
}

/* ---------- Rail state ---------- */

/** @param {Element} el */
const railOf = (el) => el.closest('[data-ccc-rail]');

/** @type {WeakMap<Element, HTMLElement>} Last tabbable entry in each rail. */
const rovingItem = new WeakMap();

/** Roving tabindex: exactly one rail item is tabbable. @param {HTMLElement} item */
function setRoving(item) {
  const root = railOf(item);
  if (!root) return;
  const previous = rovingItem.get(root);
  if (previous === item && item.tabIndex === 0) return;
  if (previous && root.contains(previous) && previous.tabIndex === 0) {
    previous.tabIndex = -1;
  } else {
    // Initialise from the rendered markup, including after a header replacement.
    for (const entry of root.querySelectorAll('[data-ccc-rail-item][tabindex="0"]')) {
      if (entry !== item) entry.tabIndex = -1;
    }
  }
  item.tabIndex = 0;
  rovingItem.set(root, item);
}

/** @type {WeakMap<Element, HTMLElement>} rail root -> active entry */
const activeTab = new WeakMap();

/**
 * Switch panes: toggle the old and new entry and pane only (no queries over
 * the panel, no measuring). Per-pane extras run after the new pane painted.
 * @param {HTMLElement} tab
 */
function activate(tab) {
  const root = railOf(tab);
  const pane = document.getElementById(tab.dataset.cccTab || '');
  if (!root || !pane) return;
  if (!tab.hasAttribute('data-active')) {
    performance.mark('ccc-switch-start');
    const prev = activeTab.get(root);
    if (!prev || !root.contains(prev) || !prev.hasAttribute('data-active')) {
      // Reconcile server defaults once; subsequent switches touch the old/new pair.
      root.querySelectorAll('[data-ccc-pane]:not([hidden])').forEach((other) => {
        if (other !== pane) /** @type {HTMLElement} */ (other).hidden = true;
      });
      root.querySelectorAll('[data-ccc-tab][data-active]').forEach((other) => {
        if (other !== tab) {
          other.removeAttribute('data-active');
          if (other.getAttribute('aria-current') === 'true') other.removeAttribute('aria-current');
        }
      });
    }
    if (prev && prev !== tab) {
      prev.removeAttribute('data-active');
      if (prev.getAttribute('aria-current') === 'true') prev.removeAttribute('aria-current');
      const prevPane = document.getElementById(prev.dataset.cccTab || '');
      if (prevPane) prevPane.hidden = true;
    }
    tab.setAttribute('data-active', '');
    tab.setAttribute('aria-current', 'true');
    pane.hidden = false;
    activeTab.set(root, tab);
    requestAnimationFrame(() =>
      setTimeout(() => {
        try {
          performance.measure('ccc-switch', 'ccc-switch-start');
        } catch {
          // Marks cleared: nothing to measure.
        }
        // "(continued)" headings need layout, so measure once, after the paint.
        if (!pane.hasAttribute('data-ccc-cont') && pane.querySelector('[data-ccc-filled]')) {
          pane.setAttribute('data-ccc-cont', '');
          markContinued(pane);
        }
      }, 0)
    );
  } else {
    activeTab.set(root, tab);
  }
  setRoving(tab);
  fillSlots(pane);
}

/** Nav items this script has opened (pointerover outside the nav skips work when empty). */
const openItems = new Set();

/** @param {Element} item  li.cdm-nav__item */
function setExpanded(item, open) {
  const top = item.querySelector(':scope > [data-ccc-top]');
  const was = top ? top.getAttribute('aria-expanded') === 'true' : false;
  if (top && was !== open) top.setAttribute('aria-expanded', String(open));
  if (open) openItems.add(item);
  else openItems.delete(item);
  // Measure once per open, and not here: a read in the pointerover handler
  // forced a full-page style and layout (~35 ms at 4x CPU) before the panel
  // could open. alignPanel measures in its animation frame instead.
  if (open && !was) {
    performance.mark('ccc-open');
    alignPanel(item);
  }
}

/**
 * Runs in the next animation frame (before that frame's style and paint, so
 * the panel's first frame is still right), reads first, then writes:
 * - --ccc-header-bottom: the panel max height is 80vh minus the header, so
 *   record where the header ends. Set on the item (the rail inherits it), and
 *   only when it changes, so the whole page does not restyle on every open.
 * - Mega panels are centred under the bar. With the full header the end items
 *   (Coffee, Sale) sit outside that centred panel, so moving the pointer
 *   straight down from them left the panel and closed it. Shift the panel
 *   (margin-left, so the open/close transforms are untouched) until it spans
 *   its top item, kept 8px inside the viewport.
 * @param {Element} item  li.cdm-nav__item
 */
function alignPanel(item) {
  const panel = item.querySelector(':scope > .cdm-dropdown--mega');
  requestAnimationFrame(() => {
    const t = item.getBoundingClientRect();
    const p = panel instanceof HTMLElement ? panel.getBoundingClientRect() : null;
    const bottom = `${Math.round(t.bottom + 6)}px`;
    if (item instanceof HTMLElement && item.style.getPropertyValue('--ccc-header-bottom') !== bottom) {
      item.style.setProperty('--ccc-header-bottom', bottom);
    }
    if (!(panel instanceof HTMLElement) || !p || !p.width) return;
    const old = parseFloat(panel.style.marginLeft) || 0;
    const left = p.left - old;
    const right = left + p.width;
    const pad = 8;
    let shift = 0;
    if (t.left - pad < left) shift = t.left - pad - left;
    else if (t.right + pad > right) shift = t.right + pad - right;
    const vw = document.documentElement.clientWidth;
    shift = Math.max(pad - left, Math.min(shift, vw - pad - right));
    const next = `${Math.round(shift)}px`;
    if (panel.style.marginLeft !== next) panel.style.marginLeft = next;
  });
}

/**
 * Close a dropdown until the pointer or focus leaves it.
 * @param {HTMLElement} item
 */
function close(item) {
  item.classList.remove('is-open');
  item.classList.add('cdm-nav__item--dismissed');
  setExpanded(item, false);
  const reset = () => item.classList.remove('cdm-nav__item--dismissed');
  item.addEventListener('pointerleave', reset, { once: true });
  item.addEventListener(
    'focusout',
    (e) => {
      if (!(e.relatedTarget instanceof Node && item.contains(e.relatedTarget))) reset();
    },
    { once: true }
  );
}

/**
 * Open a panel (toggle button, ArrowDown, Space). With focusInside, focus moves
 * to the rail's current entry, or the first link of a plain dropdown.
 * @param {HTMLElement} item  li.cdm-nav__item
 * @param {boolean} [focusInside]
 */
function openPanel(item, focusInside = true) {
  document.querySelectorAll('.ccc-nav .cdm-nav__item.is-open').forEach((el) => {
    if (el !== item) {
      el.classList.remove('is-open');
      setExpanded(el, false);
    }
  });
  item.classList.remove('cdm-nav__item--dismissed');
  item.classList.add('is-open');
  setExpanded(item, true);
  const current = item.querySelector('[data-ccc-rail-item][tabindex="0"]');
  if (current instanceof HTMLElement && current.hasAttribute('data-ccc-tab')) activate(current);
  if (!focusInside) return;
  const target = current || item.querySelector('.cdm-dropdown a[href], .cdm-dropdown button');
  if (target instanceof HTMLElement) target.focus();
}

/* ---------- Keyboard ---------- */

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** @param {KeyboardEvent} event */
function onKeydown(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Ready to Ship group tabs also appear inside the mobile drawer.
  const readyTab = target.closest('[data-ccc-ready-tab]');
  if (readyTab instanceof HTMLElement && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
    const tabs = Array.from(readyTab.parentElement?.querySelectorAll('[data-ccc-ready-tab]') || []);
    const next = tabs[(tabs.indexOf(readyTab) + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (next instanceof HTMLElement) {
      event.preventDefault();
      selectReadyTab(next);
      next.focus();
    }
    return;
  }

  if (!target.closest('.ccc-nav')) return;
  const item = target.closest('.cdm-nav__item--has-dropdown');

  if (event.key === 'Escape') {
    if (!(item instanceof HTMLElement)) return;
    event.preventDefault();
    close(item);
    // Focus returns to the disclosure toggle (or the top link if there is none).
    const back = item.querySelector(':scope > [data-ccc-top]') || item.querySelector(':scope > .cdm-nav__link');
    if (back instanceof HTMLElement) back.focus();
    return;
  }

  // Top link or its toggle: ArrowDown opens the panel and moves into it;
  // Space on the top link does the same (Space/Enter on the toggle is a click).
  const isTop = target.matches('[data-ccc-top]') || (target.matches('.cdm-nav__link') && target.parentElement === item);
  if (isTop && item instanceof HTMLElement && (event.key === 'ArrowDown' || (event.key === ' ' && !target.matches('button')))) {
    event.preventDefault();
    openPanel(item, true);
    return;
  }

  // Rail: one tab stop; arrows move along every rail item.
  const railItem = target.closest('[data-ccc-rail-item]');
  if (railItem instanceof HTMLElement) {
    const items = Array.from(railOf(railItem)?.querySelectorAll('[data-ccc-rail-item]') || []);
    const index = items.indexOf(railItem);
    let next = null;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'ArrowRight') {
      const activeTab = railOf(railItem)?.querySelector('[data-ccc-tab][data-active]');
      const pane = activeTab && document.getElementById(/** @type {HTMLElement} */ (activeTab).dataset.cccTab || '');
      // Ready to Ship's narrow-layout brew tabs stay in the DOM when hidden
      // on desktop. Skip those controls so focus reaches a visible pane link.
      let first = null;
      if (pane) {
        for (const element of pane.querySelectorAll(FOCUSABLE)) {
          if (element instanceof HTMLElement && element.getClientRects().length > 0) {
            first = element;
            break;
          }
        }
      }
      if (first instanceof HTMLElement) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (next === null) return;
    event.preventDefault();
    const to = /** @type {HTMLElement} */ (items[next]);
    if (to.hasAttribute('data-ccc-tab')) activate(to);
    else setRoving(to);
    to.focus();
    return;
  }

  // Pane: ArrowLeft returns to the rail.
  if (event.key === 'ArrowLeft' && target.closest('[data-ccc-pane]')) {
    const back = railOf(target)?.querySelector('[data-ccc-rail-item][tabindex="0"]');
    if (back instanceof HTMLElement) {
      event.preventDefault();
      back.focus();
    }
  }
}

/* ---------- Pointer: rail switching ---------- */
/*
 * Rules (deterministic, no rest timers):
 * - Entering an entry switches at once, unless the pointer is heading right
 *   into the pane (last move clearly rightward: dx > 2 and dx > |dy|).
 * - Heading into the pane: wait at most AIM_DELAY_MAX; switch early as soon as the
 *   pointer moves mostly vertically or stops (no move for STOP_DELAY). If it
 *   reaches the pane first, nothing switches.
 * - Leaving the rail into the pane never switches; coming back onto an entry
 *   switches at once (its move is leftward, so not "heading into the pane").
 */
const AIM_DELAY_MAX = 150;
const STOP_DELAY = 60;
let last = { x: 0, y: 0, t: 0 };
let prev = { x: 0, y: 0, t: 0 };
/** @type {HTMLElement | null} */
let hoverTab = null;
/** @type {HTMLElement | null} */
let pendingTab = null;
let aimTimer = 0;
let stopTimer = 0;

/** Last movement vector (current pointer position minus the previous sample). */
const moveVector = () => ({ dx: last.x - prev.x, dy: last.y - prev.y });
const headingIntoPane = () => {
  const { dx, dy } = moveVector();
  return dx > 2 && dx > Math.abs(dy);
};

function clearPending() {
  window.clearTimeout(aimTimer);
  window.clearTimeout(stopTimer);
  pendingTab = null;
}

/** Switch to the pending entry now (if the pointer is still on it). */
function commitPending() {
  const tab = pendingTab;
  clearPending();
  if (tab && hoverTab === tab) activate(tab);
}

/** @param {PointerEvent} event */
function onPointerMove(event) {
  if (event.pointerType === 'touch') return;
  if (event.clientX === last.x && event.clientY === last.y) return;
  prev = last;
  last = { x: event.clientX, y: event.clientY, t: event.timeStamp };
  if (!pendingTab) return;
  // Pending (heading into the pane): a vertical move means "this entry".
  const { dx, dy } = moveVector();
  if (Math.abs(dy) >= Math.abs(dx)) {
    commitPending();
    return;
  }
  // Still moving: restart the "stopped" check.
  window.clearTimeout(stopTimer);
  stopTimer = window.setTimeout(commitPending, STOP_DELAY);
}

/** @param {PointerEvent} event */
function onPointerOver(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || event.pointerType === 'touch') return;
  // Anywhere else on the page with nothing open: nothing to do.
  if (!openItems.size && !target.closest('.ccc-nav')) return;
  for (const item of openItems) if (!item.isConnected) openItems.delete(item);

  const navItem = target.closest('.ccc-nav .cdm-nav__item--has-dropdown');
  for (const item of openItems) {
    if (item !== navItem && !item.matches(':focus-within')) setExpanded(item, false);
  }
  if (navItem) setExpanded(navItem, true);

  const root = target.closest('[data-ccc-rail]');
  const enteredRail = root && !(event.relatedTarget instanceof Node && root.contains(event.relatedTarget));
  // Direct entry onto a tab must warm the rail too; no top-link event is guaranteed.
  if (enteredRail) loadAllSoon(root);

  const tab = target.closest('[data-ccc-tab]');
  if (!(tab instanceof HTMLElement)) {
    hoverTab = null;
    // In the pane (or anywhere else) a pending switch is cancelled.
    if (target.closest('.ccc-rail__panes')) clearPending();
    // Crossing descendants inside the same rail does not need to warm it again.
    // A failed pane still retries when re-entering the rail or selecting its tab.
    if (enteredRail) {
      const active = root.querySelector('[data-ccc-tab][data-active]');
      if (active instanceof HTMLElement) fillSlots(document.getElementById(active.dataset.cccTab || '') || root);
    }
    return;
  }
  if (hoverTab === tab) return;
  hoverTab = tab;
  // pointerover fires before the pointermove at this position: record it.
  if (event.clientX !== last.x || event.clientY !== last.y) {
    prev = last;
    last = { x: event.clientX, y: event.clientY, t: event.timeStamp };
  }
  clearPending();
  if (!headingIntoPane()) {
    performance.mark('ccc-enter');
    activate(tab);
    return;
  }
  // Heading into the pane: wait (capped), commit early on a vertical move or a stop.
  pendingTab = tab;
  aimTimer = window.setTimeout(commitPending, AIM_DELAY_MAX);
  stopTimer = window.setTimeout(commitPending, STOP_DELAY);
}

/** @param {PointerEvent} event */
function onPointerOut(event) {
  const from = event.target instanceof Element ? event.target : null;
  const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
  const navItem = from && from.closest('.ccc-nav .cdm-nav__item--has-dropdown');
  if (navItem && !(to && navItem.contains(to)) && !navItem.matches(':focus-within')) setExpanded(navItem, false);
}

/** @param {FocusEvent} event */
function onFocusIn(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const navItem = target.closest('.ccc-nav .cdm-nav__item--has-dropdown');
  // Focus inside an open panel keeps it open (the panel no longer opens on mere
  // focus of the top link: that is the toggle's job).
  if (navItem && target.closest('.cdm-dropdown') && !navItem.classList.contains('cdm-nav__item--dismissed')) {
    navItem.classList.add('is-open');
    setExpanded(navItem, true);
  }
  const tab = target.closest('[data-ccc-tab]');
  if (tab instanceof HTMLElement) activate(tab);
  else {
    const railItem = target.closest('[data-ccc-rail-item]');
    if (railItem instanceof HTMLElement) setRoving(railItem);
  }
}

/** @param {FocusEvent} event */
function onFocusOut(event) {
  const target = event.target instanceof Element ? event.target : null;
  const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
  const navItem = target && target.closest('.ccc-nav .cdm-nav__item--has-dropdown');
  if (navItem && !(to && navItem.contains(to))) {
    navItem.classList.remove('is-open');
    if (!navItem.matches(':hover')) setExpanded(navItem, false);
  }
}

/* ---------- Ready tabs, touch ---------- */

let lastPointer = 'mouse';

/** @param {MouseEvent} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Touch: first tap opens a top item / switches a rail entry, second tap follows.
  if (lastPointer === 'touch') {
    const top = target.closest('.ccc-nav .cdm-nav__item--has-dropdown > .cdm-nav__link');
    if (top) {
      const item = /** @type {HTMLElement} */ (top.parentElement);
      if (!item.classList.contains('is-open')) {
        event.preventDefault();
        document.querySelectorAll('.ccc-nav .cdm-nav__item.is-open').forEach((el) => el.classList.remove('is-open'));
        item.classList.add('is-open');
        setExpanded(item, true);
        const active = item.querySelector('[data-ccc-tab][data-active]');
        if (active instanceof HTMLElement) activate(active);
        return;
      }
    }
    const tab = target.closest('[data-ccc-tab]');
    if (tab instanceof HTMLElement && !tab.hasAttribute('data-active')) {
      event.preventDefault();
      activate(tab);
      return;
    }
    if (!target.closest('.ccc-nav')) {
      document.querySelectorAll('.ccc-nav .cdm-nav__item.is-open').forEach((el) => {
        el.classList.remove('is-open');
        setExpanded(el, false);
      });
    }
  }

  // Disclosure toggle (button): Enter, Space or click opens / closes the panel.
  const toggleBtn = target.closest('.ccc-nav [data-ccc-top]');
  if (toggleBtn && lastPointer !== 'touch') {
    const item = /** @type {HTMLElement} */ (toggleBtn.closest('.cdm-nav__item--has-dropdown'));
    event.preventDefault();
    if (item.classList.contains('is-open')) {
      close(item);
    } else {
      openPanel(item, false);
    }
    return;
  }

  const readyTab = target.closest('[data-ccc-ready-tab]');
  if (readyTab instanceof HTMLElement) {
    selectReadyTab(readyTab);
    return;
  }

  const frozenLink = target.closest('[data-ccc-ready-frozen]');
  if (frozenLink) {
    const heading = frozenLink.closest('[data-ccc-ready-panel]')?.querySelector('[data-ccc-frozen-heading]');
    if (heading instanceof HTMLElement) {
      heading.focus({ preventScroll: true });
      const scroller = heading.closest('.ccc-rail__scroll');
      if (scroller instanceof HTMLElement && scroller.scrollHeight > scroller.clientHeight) {
        scroller.scrollTop += heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 16;
      } else {
        heading.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    }
  }

}

/**
 * Ready to Ship brew tabs (narrow panes only): one section visible at a time.
 * @param {HTMLElement} tab
 */
function selectReadyTab(tab) {
  const wrap = tab.closest('[data-ccc-ready]');
  if (!wrap) return;
  wrap.querySelectorAll('[data-ccc-ready-tab]').forEach((el) => {
    const on = el === tab;
    el.setAttribute('aria-selected', String(on));
    el.setAttribute('tabindex', on ? '0' : '-1');
  });
  wrap.querySelectorAll('[data-ccc-ready-panel]').forEach((panel) => {
    panel.toggleAttribute('data-ccc-ready-inactive', panel.getAttribute('data-ccc-ready-panel') !== tab.dataset.cccReadyTab);
  });
}

if (!window.__cccMenuPreview) {
  window.__cccMenuPreview = true;
  document.addEventListener(
    'pointerdown',
    (event) => {
      lastPointer = event.pointerType || 'mouse';
    },
    { capture: true, passive: true }
  );
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerover', onPointerOver, { passive: true });
  document.addEventListener('focusin', onWarm);
  document.addEventListener('pointerout', onPointerOut, { passive: true });
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeydown);
  // CCC: pane work starts only on its own rail intent (onPointerOver/onWarm).
}
